import { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export type SelectOption = { value: string; label: string };

type SelectProps = {
  label: string;
  className?: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  resetKey: number;
};

export const ThemedSelect = ({ label, className = '', value, options, onChange, resetKey }: SelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();
  const menuId = useId();

  useEffect(() => { setOpen(false); }, [resetKey]);
  useEffect(() => {
    if (!open) return;
    selectedRef.current?.focus();
    const closeFromOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeFromOutside);
    return () => document.removeEventListener('pointerdown', closeFromOutside);
  }, [open]);

  const close = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <div ref={rootRef} className={'field entry-inline-select ' + className}>
      <span id={labelId}>{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className="entry-select-trigger"
        role="combobox"
        aria-labelledby={labelId}
        aria-controls={menuId}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
          if (event.key === 'Escape' && open) {
            event.preventDefault();
            close();
          }
        }}
      >
        <span>{options.find((option) => option.value === value)?.label ?? options[0]?.label}</span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>
      {open && (
        <div id={menuId} className="entry-select-menu" role="listbox" aria-labelledby={labelId}>
          {options.map((option) => (
            <button
              key={option.value}
              ref={option.value === value ? selectedRef : undefined}
              type="button"
              className="entry-select-option"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                close();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  close();
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

type DateProps = {
  label: string;
  className?: string;
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
  resetKey: number;
};

const dateKey = (date: Date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

const fromDateKey = (value: string) => {
  const parts = value.split('-').map(Number);
  return parts.length === 3 && parts.every(Number.isFinite)
    ? new Date(parts[0], parts[1] - 1, parts[2])
    : new Date();
};

const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const ThemedDateField = ({ label, className = '', value, required = false, onChange, resetKey }: DateProps) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();
  const year = month.getFullYear();
  const monthNumber = month.getMonth();
  const leading = new Date(year, monthNumber, 1).getDay();
  const numberOfDays = new Date(year, monthNumber + 1, 0).getDate();
  const cells = Array.from({ length: Math.ceil((leading + numberOfDays) / 7) * 7 }, (_, index) => index - leading + 1);

  useEffect(() => { setOpen(false); }, [resetKey]);

  const close = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const choose = (nextValue: string) => {
    onChange(nextValue);
    close();
  };

  return (
    <div className={'field ' + className}>
      <span id={labelId}>{label}</span>
      <input className="entry-native-date" type="date" required={required} value={value} aria-labelledby={labelId} onChange={(event) => onChange(event.target.value)} />
      <button ref={triggerRef} className="entry-date-trigger" type="button" aria-labelledby={labelId} aria-haspopup="dialog" aria-expanded={open} onClick={() => { const selected = fromDateKey(value); setMonth(new Date(selected.getFullYear(), selected.getMonth(), 1)); setOpen(true); }}>
        <span>{value ? new Intl.DateTimeFormat(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(fromDateKey(value)) : 'Choose date'}</span>
        <CalendarDays size={19} aria-hidden="true" />
      </button>
      {open && (
        <div className="entry-picker-overlay" onClick={close}>
          <div className="entry-picker-sheet entry-date-sheet" role="dialog" aria-modal="true" aria-label={'Choose ' + label.toLowerCase()} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); } }}>
            <div className="entry-picker-heading"><strong>{label}</strong><button type="button" onClick={close} aria-label="Close calendar">Close</button></div>
            <div className="entry-month-navigation">
              <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(year, monthNumber - 1, 1))}><ChevronLeft size={19} /></button>
              <strong aria-live="polite">{new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(month)}</strong>
              <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(year, monthNumber + 1, 1))}><ChevronRight size={19} /></button>
            </div>
            <div className="entry-date-grid">
              {weekdays.map((day) => <span key={day} className="entry-weekday">{day}</span>)}
              {cells.map((day, index) => day < 1 || day > numberOfDays
                ? <span key={index} aria-hidden="true" />
                : <button key={index} type="button" aria-label={new Intl.DateTimeFormat(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(year, monthNumber, day))} aria-pressed={value === dateKey(new Date(year, monthNumber, day))} onClick={() => choose(dateKey(new Date(year, monthNumber, day)))}>{day}</button>
              )}
            </div>
            <div className="entry-date-actions">
              {!required && <button type="button" onClick={() => choose('')}>Clear</button>}
              <button type="button" onClick={() => choose(dateKey(new Date()))}>Today</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type TimeProps = {
  label: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  resetKey: number;
  hint?: string;
};

const timeParts = (value: string) => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (match) return { hour: match[1], minute: String(Math.floor(Number(match[2]) / 5) * 5).padStart(2, '0') };
  const now = new Date();
  return { hour: String(now.getHours()).padStart(2, '0'), minute: String(Math.floor(now.getMinutes() / 5) * 5).padStart(2, '0') };
};

const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minutes = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, '0'));

export const ThemedTimeField = ({ label, className = '', value, onChange, resetKey, hint }: TimeProps) => {
  const initial = timeParts(value);
  const [open, setOpen] = useState(false);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedHourRef = useRef<HTMLButtonElement>(null);
  const selectedMinuteRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();

  useEffect(() => { setOpen(false); }, [resetKey]);
  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => {
      selectedHourRef.current?.scrollIntoView({ block: 'center' });
      selectedMinuteRef.current?.scrollIntoView({ block: 'center' });
    });
  }, [open]);

  const close = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const openPicker = () => {
    const next = timeParts(value);
    setHour(next.hour);
    setMinute(next.minute);
    setOpen(true);
  };

  return (
    <div className={'field entry-time-field ' + className}>
      <span id={labelId}>{label}</span>
      <button ref={triggerRef} className="entry-time-trigger" type="button" aria-labelledby={labelId} aria-haspopup="dialog" aria-expanded={open} onClick={openPicker}>
        <span>{value || 'Choose time'}</span>
        <Clock size={19} aria-hidden="true" />
      </button>
      {hint && <small>{hint}</small>}
      {open && (
        <div className="entry-picker-overlay" onClick={close}>
          <div className="entry-picker-sheet entry-time-sheet" role="dialog" aria-modal="true" aria-labelledby={labelId} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); } }}>
            <div className="entry-picker-heading"><strong>{label}</strong><button type="button" onClick={close}>Close</button></div>
            <div className="entry-time-columns">
              <div className="entry-time-column"><strong>Hour</strong><div className="entry-time-list">{hours.map((option) => <button ref={option === hour ? selectedHourRef : undefined} key={option} type="button" aria-pressed={option === hour} onClick={() => setHour(option)}>{option}</button>)}</div></div>
              <span className="entry-time-separator" aria-hidden="true">:</span>
              <div className="entry-time-column"><strong>Minute</strong><div className="entry-time-list">{minutes.map((option) => <button ref={option === minute ? selectedMinuteRef : undefined} key={option} type="button" aria-pressed={option === minute} onClick={() => setMinute(option)}>{option}</button>)}</div></div>
            </div>
            <div className="entry-date-actions"><button type="button" onClick={() => { onChange(''); close(); }}>Clear</button><button type="button" onClick={() => { onChange(`${hour}:${minute}`); close(); }}>Apply</button></div>
          </div>
        </div>
      )}
    </div>
  );
};
